<template>
	<div>
		<Loader v-model="loading"></Loader>
		<LowConfidenceDialog ref="lowConfidenceDialog" @scan-again="back" @continue="continueWithLowConfidence"/>

		<div class="container my-4">
			<div class="row justify-content-center">
				<div class="col-lg-10 col-12">
					<!-- Content Area -->
					<div class="content-area">
						<!-- Image Preview -->
						<div class="image-preview mb-4">
							<div class="card shadow-sm">
								<div class="card-body p-0">
									<canvas v-show="!isPdf" id="imgSrc" ref="imgSrc" class="w-100 rounded-3"></canvas>
									<embed v-if="isPdf" :src="base64Image" type="application/pdf" class="w-100 rounded-3 pdf-preview">
								</div>
							</div>
						</div>

						<!-- Action Buttons -->
						<!-- Form Area -->
						<div class="form-area" v-if="hasExtractedData">
							<n-collapse :expanded-names="expendDatas" @update:expanded-names="toggleExpand"
										class="bg-white rounded-3 shadow-sm">
								<n-collapse-item name="1">
									<template #header>
										<div class="d-flex align-items-center py-2">
											<i class="fas fa-file-alt me-2"></i>
											<span class="fw-medium">Données extraites</span>
										</div>
									</template>

									<div class="p-3">
										<n-form :label-width="120">
											<n-form-item label="Date de la facture">
												<n-input :status="showErrors&&hasErrors.date ? 'error': ''"
														 v-model:value="ticketDatas.date.value" type="date"/>
											</n-form-item>

											<n-form-item label="Montant HT">
												<n-input v-model:value="ticketDatas.totalht.value" type="number"/>
											</n-form-item>

											<n-form-item label="Montant TTC">
												<n-input v-model:value="ticketDatas.totalttc.value" type="number"/>
											</n-form-item>

											<n-form-item label="Fournisseur">
												<n-input v-model:value="ticketDatas.fournisseurname.value" type="text"/>
											</n-form-item>

											<n-form-item label="Adresse">
												<n-input v-model:value="ticketDatas.fournisseuradresse.value"
														 type="text"/>
											</n-form-item>

											<n-form-item label="Pays">
												<n-select v-model:value="ticketDatas.pays.value" :options="pays"/>
											</n-form-item>

											<n-form-item label="Mode de paiement">
												<n-select v-model:value="ticketDatas.modepaiement.value"
														  :options="modedereglements"/>
											</n-form-item>

											<n-form-item label="Raison">
												<n-input v-model:value="ticketDatas.comment.value" type="textarea"/>
											</n-form-item>

											<n-form-item v-if="ticketDatas.signalerprobleme.value" label="Signaler un problème">
												<n-switch v-model:value="ticketDatas.signalerprobleme.value"/>
											</n-form-item>

											<n-form-item v-if="ticketDatas.commentaireprobleme.value"
														 label="Description problème">
												<n-input v-model:value="ticketDatas.commentaireprobleme.value"
														 type="textarea"/>
											</n-form-item>
										</n-form>

										<div class="mt-4 d-flex justify-content-between">
											<n-button @click="back()" class="me-2">
												<template #icon>
													<i class="fas fa-arrow-left me-2"></i>
												</template>
												Retour
											</n-button>
											<n-button v-if="ticketDatas" type="primary" @click="validate()">
												<template #icon>
													<i class="fas fa-check me-2"></i>
												</template>
												Valider les données
											</n-button>
										</div>
									</div>
								</n-collapse-item>
							</n-collapse>
						</div>
					</div>
				</div>
			</div>
			<div v-if="!hasExtractedData" class="d-flex justify-content-between mt-4 scan-actions-bar">
				<button
					@click="back()"
					class="btn btn-light d-inline-flex align-items-center">
					<i class="fas fa-arrow-left me-2"></i>
					<span>Retour</span>
				</button>
				<div>
					<button
						@click="retakePhoto()"
						class="btn btn-light d-inline-flex align-items-center me-2">
						<i class="fas fa-camera me-2"></i>
						<span>Refaire la photo</span>
					</button>
					<n-button v-if="getIsDev" type="warning" @click="startRecognizer(true)">
						<template #icon>
							<i class="fas fa-bug me-2"></i>
						</template>
						Test
					</n-button>
					<n-button id="process" type="primary" class="ms-2" @click="startRecognizer(false)">
						<template #icon>
							<i class="fas fa-cogs me-2"></i>
						</template>
						Analyser
					</n-button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>

import axios from "axios";
import {logError, logInfo} from "../../plugins/logger.js";
import Loader from "../Loader.vue";
import {mapActions, mapGetters} from "vuex";
import fakeFields from "../../noteFraisFields.json";
import AzureFieldsExtractor from "./AzureFieldsExtractor.js";

const communicationFields = [
	'user',
	'pays',
	'comment',
	'signalerprobleme',
	'commentaireprobleme',
	'modepaiement',
	'fournisseurtva',
	'fournisseursiret',
	'fournisseuremail',
	'fournisseursite',
	'fournisseurtelephone',
	'fournisseurname',
	'fournisseuradresse',
	'date',
];

const financialFields = [
	'totalht',
	'totaltva',
	'totalttc',
];

const vatFields = [
	'tva_%1',
	'tva_%2',
	'tva_%3',
	'tva_total1',
	'tva_total2',
	'tva_total3',
];


function getFieldsTemplate(category) {
	switch (category) {
		case 'restaurant':
		case 'restaurantclient':
			return restaurantFields;
		case 'fuel':
			return fuelFields;
		case 'transportation':
			return transportationFields;
		default:
			return miscFields;
	}
}

const restaurantFields = [...communicationFields, ...financialFields, ...vatFields];
const fuelFields = [...communicationFields, ...financialFields, ...vatFields, 'km', 'litre'];
const transportationFields = [...communicationFields, ...financialFields, ...vatFields];
const miscFields = [...communicationFields, ...financialFields, ...vatFields];

import {useDialog, NButton} from 'naive-ui'

import LowConfidenceDialog from './LowConfidenceDialog.vue'

export default {
	components: {
		Loader,
		LowConfidenceDialog,
		NButton
	},
	data() {
		return {
			showErrors: false,
			lastFields: null,
			confidenceLimit: .75,
			base64Image: null,
			dataValidated: false,
			showDatas: false,
			loading: false,
			ticketDatas: null,
			toEdit: null,
			expendDatas: [],
			currentFakeAnalyzerState: false,
			hasAnalyzed: false,
			pays: [
				{label: '🇱🇺 Luxembourg', value: 'Luxembourg'},
				{label: '🇫🇷 France', value: 'France'},
				{label: '🇩🇪 Allemagne', value: 'Allemagne'},
				{label: '🇧🇪 Belgique', value: 'Belgique'},
				{label: '🇪🇸 Espagne', value: 'Espagne'},
			],
			dataHidden: true,
		}
	},
	beforeMount() {
		if (this.$route.params.id) {
			this.ticketDatas = this.getTicketDatas;
		} else {

			if (this.getTicketDatas && Object.keys(this.getTicketDatas).length > 0) {
				this.ticketDatas = this.getTicketDatas;
			} else {

				let emptyDatas = {};
				getFieldsTemplate(this.getCategory).forEach(function (key) {
					emptyDatas[key] = {
						name: key,
						value: '',
						coordinates: null,
					};
				});

				emptyDatas.user.value = this.getUser.id;
				emptyDatas.modepaiement.value = this.getUser.default_payment_method || 'Perso';
				this.ticketDatas = emptyDatas;
			}
		}
	},
	mounted() {
		this.base64Image = this.getBase64Img;
		if (this.isPdf) return;

		let canvas = this.$refs.imgSrc;
		let ctx = canvas.getContext("2d");
		let img = new Image();
		img.onload = () => {
			canvas.width = img.width;
			canvas.height = img.height;
			ctx.drawImage(img, 0, 0);

			if (this.$route.params.id) {
				for (let field of Object.values(this.ticketDatas)) {
					if (field.coordinates) {
						this.draw(field.coordinates);
					}
				}
			}
		};
		img.src = this.base64Image;
	},
	computed: {
		hasExtractedData() {
			return this.hasAnalyzed;
		},
		isPdf() {
			return typeof this.base64Image === 'string' && this.base64Image.startsWith('data:application/pdf');
		},
		...mapGetters(['getBase64Img',
			'getCategory',
			'getUser',
			'getIsDev',
			'getTicketDatas']),
		modedereglements() {
			return [
				{label: 'Perso', value: 'Perso'},
				{label: 'CB BIL', value: 'CB BIL'},
				{label: 'CB BPLC', value: 'CB BPLC'},
			]
		},
		hasErrors() {
			return {
				date: this.ticketDatas.date.value === '',
			}
		}
	},
	methods: {
		...mapActions(['setTicketDatas']),
		async getImageBlurScore() {
			try {
				const base64Image = this.getBase64Img;
				if (!base64Image) {
					console.error('Image non trouvée');
					return 1;
				}

				const img = new Image();
				img.src = base64Image;

				await new Promise((resolve, reject) => {
					img.onload = resolve;
					img.onerror = reject;
				});

				const canvas = document.createElement('canvas');
				canvas.width = img.width;
				canvas.height = img.height;
				const ctx = canvas.getContext('2d');

				ctx.drawImage(img, 0, 0);
				const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
				const data = imageData.data;

				// Calculer la variance locale sur des blocs de 3x3 pixels
				let totalVariance = 0;
				let blockCount = 0;
				const blockSize = 3;

				for (let y = 0; y < canvas.height - blockSize; y += blockSize) {
					for (let x = 0; x < canvas.width - blockSize; x += blockSize) {
						// Calculer la moyenne du bloc
						let sum = 0;
						let values = [];

						// Collecter les valeurs du bloc
						for (let by = 0; by < blockSize; by++) {
							for (let bx = 0; bx < blockSize; bx++) {
								const idx = ((y + by) * canvas.width + (x + bx)) * 4;
								const intensity = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
								sum += intensity;
								values.push(intensity);
							}
						}

						const mean = sum / (blockSize * blockSize);

						// Calculer la variance du bloc
						let variance = 0;
						for (const value of values) {
							variance += Math.pow(value - mean, 2);
						}
						variance /= (blockSize * blockSize);

						totalVariance += variance;
						blockCount++;
					}
				}

				// Calculer la variance moyenne
				const averageVariance = totalVariance / blockCount;
				console.log('Variance moyenne:', averageVariance);

				// Seuils basés sur les valeurs observées
				const MIN_VARIANCE = 5;  // Seuil pour les images floues
				const MAX_VARIANCE = 50; // Seuil pour les images nettes

				// Normaliser le score entre 0 et 1
				let score = (averageVariance - MIN_VARIANCE) / (MAX_VARIANCE - MIN_VARIANCE);

				// Limiter le score entre 0 et 1
				score = Math.min(1, Math.max(0, score));

				// Convertir en score de netteté sur 100
				score = Math.round(score * 100);

				console.log('Score final:', score);
				return score;

			} catch (error) {
				console.error('Erreur dans getImageBlurScore:', error);
				return 1;
			}
		},
		async continueWithLowConfidence() {
			// Lancer le scan Azure avec le même état que startRecognizer
			await this.performAzureScan(this.currentFakeAnalyzerState);
		},
		drawWord(coordinates) {

			let canvas = this.$refs.imgSrc;
			let ctx = canvas.getContext("2d");

			ctx.fillStyle = 'rgba(69,60,60,0.16)';
			ctx.beginPath();
			ctx.moveTo(coordinates[0], coordinates[1]);
			ctx.lineTo(coordinates[2], coordinates[3]);
			ctx.lineTo(coordinates[4], coordinates[5]);
			ctx.lineTo(coordinates[6], coordinates[7]);
			ctx.closePath();
			ctx.fill();
		},
		draw(coordinates) {

			let canvas = this.$refs.imgSrc;
			let ctx = canvas.getContext("2d");

			ctx.fillStyle = 'rgba(255,242,19,0.2)';
			ctx.beginPath();
			ctx.moveTo(coordinates[0], coordinates[1]);
			ctx.lineTo(coordinates[2], coordinates[3]);
			ctx.lineTo(coordinates[4], coordinates[5]);
			ctx.lineTo(coordinates[6], coordinates[7]);
			ctx.closePath();
			ctx.fill();
		},
		getFakeAzureRecognizer() {

			return new Promise((resolve, reject) => {
				resolve(fakeFields);
			});
		},
		getAzureRecognizer() {
			// Toute branche de cette promesse doit finir en resolve ou reject :
			// une erreur réseau non rattrapée la laissait pending indéfiniment,
			// et l'écran restait bloqué sur le loader, sans bouton cliquable.
			return new Promise((resolve, reject) => {
				let imgData = this.base64Image.replace(/^data:(image\/(png|jpeg)|application\/pdf);base64,/, "");
				const payloadKb = Math.round(imgData.length / 1024);
				logInfo('ocr.azure', 'envoi analyse', {payload_kb: payloadKb});

				// Azure rejette les base64Source au-delà de ~4 Mo.
				if (payloadKb > 4000) {
					logError('ocr.azure', `image trop lourde pour Azure (${payloadKb} Ko)`, {payload_kb: payloadKb});
				}

				const headers = {
					'Ocp-Apim-Subscription-Key': '5d4cafe974264f7a8955cd2b94b954fa',
					'Content-Type': 'application/json',
				};

				axios.post("https://franc1.cognitiveservices.azure.com/formrecognizer/documentModels/inv16-rec12:analyze?api-version=2023-07-31&locale=fr", {base64Source: imgData}, {headers}).then(res => {
					const url = res.headers['operation-location'];
					if (!url) {
						logError('ocr.azure', 'operation-location absent de la réponse', {payload_kb: payloadKb});
						reject(new Error("Réponse Azure inattendue"));
						return;
					}

					// Filet de sécurité : sans borne, le polling tourne sans fin si
					// l'analyse ne passe jamais à "succeeded".
					const MAX_POLLS = 45; // ~90 s
					let polls = 0;

					const interval = setInterval(() => {
						polls++;
						if (polls > MAX_POLLS) {
							clearInterval(interval);
							logError('ocr.azure', 'délai dépassé pendant le polling', {polls, payload_kb: payloadKb});
							reject(new Error("L'analyse a pris trop de temps"));
							return;
						}

						axios.get(url, {headers}).then(res => {
							const response = res.data;
							if (response.status === 'succeeded') {
								clearInterval(interval);
								if (!response.analyzeResult || !response.analyzeResult.documents || !response.analyzeResult.documents[0]) {
									logError('ocr.azure', 'analyse sans document exploitable', {payload_kb: payloadKb});
									reject(new Error("Aucune donnée reconnue sur le document"));
									return;
								}
								logInfo('ocr.azure', 'analyse réussie', {polls, payload_kb: payloadKb});
								resolve(response);
							} else if (response.status === 'failed') {
								clearInterval(interval);
								logError('ocr.azure', 'analyse en échec côté Azure', {
									payload_kb: payloadKb,
									error: response.error ? JSON.stringify(response.error).slice(0, 500) : null,
								});
								reject(new Error("L'analyse du document a échoué"));
							}
						}).catch(e => {
							clearInterval(interval);
							logError('ocr.azure.poll', e, {polls, status: e.response ? e.response.status : null});
							reject(e);
						});
					}, 2000);
				}).catch(e => {
					logError('ocr.azure.submit', e, {
						payload_kb: payloadKb,
						status: e.response ? e.response.status : null,
						detail: e.response && e.response.data ? JSON.stringify(e.response.data).slice(0, 500) : null,
					});
					reject(e);
				});
			});
		},
		parseFieldsAndDraw(resultfiedsandwords) {

			let self = this;
			const {fields, words} = resultfiedsandwords;

			let fieldsTemplate = getFieldsTemplate(this.getCategory);

			// pour chaque champ de la liste de template
			let result = fieldsTemplate.reduce((acc, curr) => {

				// si le champs est trouvé dans le pdf
				if (fields[curr]) {

					acc[curr] = fields[curr];
					if (fields[curr].boundgregions[0]?.polygon) {
						self.draw(fields[curr].boundgregions[0].polygon);
					}

				} else {
					// sinon on ajoute un champs comme il est dans le template
					acc[curr] = {
						name: curr,
						value: self.ticketDatas[curr].value,
						coordinates: null,
					};
				}

				return acc;
			}, {});

			//remplit le champ pays par rapport au numero de TVA de la facture
			if (result.pays.value === '') {
				//si FR france , si LU Luxembourg , si BE Belgique , si ES Espagne
				switch (result.fournisseurtva.value.substring(0, 2)) {
					case 'FR':
						result.pays.value = 'France';
						break;
					case 'LU':
						result.pays.value = 'Luxembourg';
						break;
					case 'BE':
						result.pays.value = 'Belgique';
						break;
					case 'ES':
						result.pays.value = 'Espagne';
						break;
				}

			}

			// verification si un taux de tva_%1, tva_%2 ou tva_%3 est à 20%, ca sera la france, 17% le luxembourg
			// converti en string pour comparer
			if (result.pays.value === '') {
				if (Number(result['tva_%1'].value) === 20 || Number(result['tva_%2'].value) === 20 || Number(result['tva_%3'].value) === 20) {
					result.pays.value = 'France';
				} else if (Number(result['tva_%1'].value) === 17 || Number(result['tva_%2'].value) === 17 || Number(result['tva_%3'].value) === 17) {
					result.pays.value = 'Luxembourg';
				} else if (Number(result['tva_%1'].value) === 3 || Number(result['tva_%2'].value) === 3 || Number(result['tva_%3'].value) === 3) {
					result.pays.value = 'Luxembourg';
				} else if (Number(result['tva_%1'].value) === 10 || Number(result['tva_%2'].value) === 10 || Number(result['tva_%3'].value) === 10) {
					result.pays.value = 'France';
				}

			}

			// ajoute tous les mots trouvés sur la page
			Object.keys(words).forEach(function (key) {
				let wordpage = words[key];
				wordpage.forEach(function (word) {
					if (word.polygon) {
						self.drawWord(word.polygon);
					}
				});

			})

			return result;
		},
		async performAzureScan(usefakeAzureRecognizer) {
			this.loading = 'Chargement';
			let self = this;
			self.expendDatas = "0";

			let promise = null;
			if (usefakeAzureRecognizer) {
				promise = this.getFakeAzureRecognizer();
			} else {
				promise = this.getAzureRecognizer();
			}

			promise.then(fields => {
				this.lastFields = fields;
				const extractedFields = AzureFieldsExtractor.extractFields(fields);
				this.ticketDatas = this.parseFieldsAndDraw(extractedFields);
				self.expendDatas = ["1"];
				this.hasAnalyzed = true;
				this.loading = false;
			}).catch(e => {
				this.loading = false;
				logError('ocr.scan', e instanceof Error ? e : String(e), {fake: !!usefakeAzureRecognizer});
				alert("L'analyse du justificatif a échoué : " + (e && e.message ? e.message : "erreur inconnue") + ". Vous pouvez saisir les données à la main.");
			});
		},
		async startRecognizer(usefakeAzureRecognizer) {
			this.loading = 'Chargement';

			// Sauvegarder l'état pour continueWithLowConfidence
			this.currentFakeAnalyzerState = usefakeAzureRecognizer;

			// Le contrôle de netteté ne s'applique qu'aux images (pas de rendu canvas pour un PDF)
			if (this.isPdf) {
				await this.performAzureScan(usefakeAzureRecognizer);
				return;
			}

			// Vérifier la netteté de l'image
			const sharpnessScore = await this.getImageBlurScore();
			console.log('Score de netteté:', sharpnessScore);

			if (sharpnessScore < 40) { // Seuil minimum de netteté
				this.loading = false;
				this.$refs.lowConfidenceDialog.open();
				return;
			}

			await this.performAzureScan(usefakeAzureRecognizer);
		},
		validate() {
			this.showErrors = true;

			if (Object.values(this.hasErrors).includes(true)) {
				return;
			}

			this.setTicketDatas(this.ticketDatas);
			this.$emit('changePath', 'recapNote');
		},
		toggleExpand(names) {
			this.expendDatas = names;
		},
		back() {
			this.hasAnalyzed = false;
			this.expendDatas = [];
			if (this.$route.params.id) {
				this.$emit('changePath', 'categories');
			} else {
				this.$emit('changePath', 'capture');
			}
		},
		retakePhoto() {
			this.hasAnalyzed = false;
			this.expendDatas = [];
			this.$emit('changePath', 'capture');
		}
	}
}

</script>
<style scoped>
.validation-container {
	background-color: #f8f9fa;
}

.image-preview canvas {
	max-height: 70vh;
	object-fit: contain;
	background-color: white;
}

.pdf-preview {
	height: 70vh;
	border: none;
	display: block;
}

@media (max-width: 768px) {
	.image-preview canvas {
		max-height: 45vh;
	}

	.pdf-preview {
		height: 45vh;
	}

	.scan-actions-bar {
		position: sticky;
		bottom: 0;
		background-color: #f8f9fa;
		padding: 0.75rem 0;
		margin-top: 0 !important;
		box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.08);
	}
}

.action-buttons .btn {
	transition: all 0.2s ease;
}

.action-buttons .btn:hover {
	transform: translateY(-1px);
}

.action-buttons .btn:active {
	transform: translateY(0);
}

.form-area {
	position: relative;
	z-index: 1;
}

:deep(.n-collapse) {
	background-color: transparent !important;
	border: none !important;
}

:deep(.n-collapse-item) {
	border: none !important;
}

:deep(.n-collapse-item__header) {
	padding: 0.75rem 1rem !important;
	border-bottom: 1px solid #dee2e6 !important;
}

:deep(.n-form-item) {
	margin-bottom: 1.5rem;
}

:deep(.n-input) {
	border-radius: 0.375rem !important;
}

:deep(.n-input:focus) {
	box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25) !important;
}

:deep(.n-switch) {
	margin-top: 0.5rem;
}
</style>